// ChartSkeletons.jsx
import React from "react";
import { Box, Skeleton ,Grid} from "@mui/material";
import { useTranslation } from "react-i18next";

// Base skeleton wrapper component
const SkeletonWrapper = ({ children, title }) => {
  return (
    <Box
    //   sx={{
    //     p: 3,
    //     border: "1px solid #e0e0e0",
    //     borderRadius: "8px",
    //     backgroundColor: "white",
    //     minHeight: "350px",
    //     display: "flex",
    //     flexDirection: "column",
    //     boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    //   }}
    >
      {/* Title Skeleton */}
      <Box sx={{ mb: 2, textAlign: "center" }}>
        <Skeleton 
          variant="text" 
          width="60%" 
          height={32} 
          sx={{ 
            mx: "auto",
            bgcolor: "grey.200",
          }} 
        />
      </Box>
      
      {/* Chart Content */}
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {children}
      </Box>
    </Box>
  );
};

// Bar Chart Skeleton
export const BarChartSkeleton = () => {
  return (
    <SkeletonWrapper>
      <Box sx={{ width: "100%", height: 200, display: "flex", alignItems: "end", gap: 1, px: 2 }}>
        {[80, 120, 60, 140, 100, 90, 110].map((height, index) => (
          <Box key={index} sx={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={height}
              sx={{
                bgcolor: "grey.200",
                borderRadius: "4px 4px 0 0",
                animation: `pulse 1.5s ease-in-out ${index * 0.1}s infinite`,
              }}
            />
            <Skeleton variant="text" width="80%" height={16} sx={{ mt: 0.5 }} />
          </Box>
        ))}
      </Box>
    </SkeletonWrapper>
  );
};

// Line Chart Skeleton
export const LineChartSkeleton = () => {
  return (
    <SkeletonWrapper>
      <Box sx={{ width: "100%", height: 200 }}>
        <svg width="100%" height="100%" viewBox="0 0 320 200">
          <defs>
            <linearGradient id="shimmer" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#f0f0f0">
                <animate
                  attributeName="stop-color"
                  values="#f0f0f0;#e0e0e0;#f0f0f0"
                  dur="1.5s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="50%" stopColor="#e0e0e0">
                <animate
                  attributeName="stop-color"
                  values="#e0e0e0;#d0d0d0;#e0e0e0"
                  dur="1.5s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="100%" stopColor="#f0f0f0">
                <animate
                  attributeName="stop-color"
                  values="#f0f0f0;#e0e0e0;#f0f0f0"
                  dur="1.5s"
                  repeatCount="indefinite"
                />
              </stop>
            </linearGradient>
          </defs>
          
          {/* Axes */}
          <line x1="40" y1="20" x2="40" y2="170" stroke="#e0e0e0" strokeWidth="2"/>
          <line x1="40" y1="170" x2="300" y2="170" stroke="#e0e0e0" strokeWidth="2"/>
          
          {/* Data line */}
          <path
            d="M 60,140 Q 100,80 140,100 T 220,60 T 280,120"
            fill="none"
            stroke="url(#shimmer)"
            strokeWidth="3"
            strokeLinecap="round"
          />
          
          {/* Data points */}
          {[60, 140, 220, 280].map((x, i) => (
            <circle
              key={i}
              cx={x}
              cy={[140, 100, 60, 120][i]}
              r="4"
              fill="url(#shimmer)"
            />
          ))}
        </svg>
        
        {/* X-axis labels */}
        <Box sx={{ display: "flex", justifyContent: "space-around", mt: 1 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="text" width={30} height={16} />
          ))}
        </Box>
      </Box>
    </SkeletonWrapper>
  );
};

// Pie Chart Skeleton
export const PieChartSkeleton = () => {
  return (
    <SkeletonWrapper>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
        <Skeleton 
          variant="circular" 
          width={160} 
          height={160} 
          sx={{ 
            bgcolor: "grey.200",
            animation: "pulse 1.5s ease-in-out 0.5s infinite"
          }}
        />
        <Box sx={{ display: "flex", gap: 2 }}>
          <Skeleton variant="rectangular" width={60} height={12} />
          <Skeleton variant="rectangular" width={60} height={12} />
          <Skeleton variant="rectangular" width={60} height={12} />
        </Box>
      </Box>
    </SkeletonWrapper>
  );
};

// Main Chart Skeleton Component (can determine type automatically)
export const ChartSkeleton = ({ chartType = "barGraph", title }) => {
  const { t } = useTranslation();
  
  const renderSkeletonByType = () => {
    switch (chartType) {
      case "pieChart":
        return <PieChartSkeleton />;
      case "lineGraph":
        return <LineChartSkeleton />;
      case "barGraph":
      default:
        return <BarChartSkeleton />;
    }
  };

  return renderSkeletonByType();
};

// Multi-Bar Chart Skeleton (for charts with multiple data series like halt/Total Trips )
export const MultiBarChartSkeleton = () => {
  return (
    <SkeletonWrapper>
      <Box sx={{ width: "100%", height: 200, display: "flex", alignItems: "end", gap: 1, px: 2 }}>
        {[1, 2, 3, 4, 5].map((groupIndex) => (
          <Box key={groupIndex} sx={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1, gap: 0.5 }}>
            {/* Multiple bars per group */}
            <Box sx={{ display: "flex", gap: 0.5, alignItems: "end" }}>
              <Skeleton
                variant="rectangular"
                width={12}
                height={80 + groupIndex * 15}
                sx={{
                  bgcolor: "grey.200",
                  borderRadius: "2px 2px 0 0",
                  animation: `pulse 1.5s ease-in-out ${groupIndex * 0.1}s infinite`,
                }}
              />
              <Skeleton
                variant="rectangular"
                width={12}
                height={60 + groupIndex * 10}
                sx={{
                  bgcolor: "grey.300",
                  borderRadius: "2px 2px 0 0",
                  animation: `pulse 1.5s ease-in-out ${groupIndex * 0.1 + 0.2}s infinite`,
                }}
              />
            </Box>
            <Skeleton variant="text" width="90%" height={16} sx={{ mt: 0.5 }} />
          </Box>
        ))}
      </Box>
    </SkeletonWrapper>
  );
};

// Loading Grid Skeleton (for showing multiple chart skeletons in a grid)
export const ChartGridSkeleton = ({ count = 6, chartTypes = ["barGraph", "lineGraph", "pieChart"] }) => {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => {
        const randomChartType = chartTypes[index % chartTypes.length];
        return (
          <Grid item xs={12} md={6} lg={4} key={`skeleton-${index}`}>
            <ChartSkeleton chartType={randomChartType} />
          </Grid>
        );
      })}
    </>
  );
};

export default ChartSkeleton;